import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type { Granularity } from '@/hooks/useAnalytics';
import {
  COLORS,
  calcTickInterval,
  customTooltipItemStyle,
  customTooltipLabelStyle,
  customTooltipStyle,
  fillTimeSeriesGaps,
  formatDateLabel,
  getGranularityLabel,
} from './chart-utils';

interface ActiveUsersChartProps {
  unfilteredActiveUsersData: Array<{ date: string; value: number }> | undefined;
  dateFrom: Date;
  dateTo: Date;
  granularity: Granularity;
}

export const ActiveUsersChart = ({ unfilteredActiveUsersData, dateFrom, dateTo, granularity }: ActiveUsersChartProps) => {
  const granularityLabel = getGranularityLabel(granularity);

  const filledActiveUsers = useMemo(() =>
    unfilteredActiveUsersData ? fillTimeSeriesGaps(
      unfilteredActiveUsersData.map(point => ({
        date: point.date,
        value: point.value,
      })),
      dateFrom,
      dateTo,
      { value: 0 },
      granularity
    ) : [],
    [unfilteredActiveUsersData, dateFrom, dateTo, granularity]
  );

  const activeUserCountChartData = useMemo(() =>
    filledActiveUsers.map(point => ({
      date: formatDateLabel(point.date, granularity),
      'Active Users': point.value,
    })),
    [filledActiveUsers, granularity]
  );

  const tickInterval = useMemo(
    () => calcTickInterval(activeUserCountChartData.length),
    [activeUserCountChartData.length]
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Active Users</CardTitle>
        <CardDescription>
          {granularityLabel} number of active users across all matters
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={activeUserCountChartData}>
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="#E5E7EB"
                strokeOpacity={0.5}
              />
              <XAxis
                dataKey="date"
                fontSize={12}
                interval={tickInterval}
                textAnchor="end"
                height={60}
              />
              <YAxis fontSize={12} allowDecimals={false} />
              <Tooltip
                formatter={(value) => Number(value).toLocaleString()}
                contentStyle={customTooltipStyle}
                labelStyle={customTooltipLabelStyle}
                itemStyle={customTooltipItemStyle}
                cursor={false}
              />
              <Bar
                dataKey="Active Users"
                fill={COLORS.primary.main}
                radius={[4, 4, 0, 0]}
                animationDuration={800}
                animationEasing="ease-out"
                activeBar={{
                  strokeWidth: 1,
                  stroke: COLORS.primary.dark,
                }}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};
